import { useState } from 'react'
import { Link } from 'react-router-dom'
import { MessageSquare, ChevronRight, Loader2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Select } from '@/components/ui/select'
import { Loading } from '@/components/shared/Loading'
import { Pagination } from '@/components/shared/Pagination'
import { useThreads } from '@/hooks/useChats'
import { formatDateTime, formatRelativeTime } from '@/utils/formatDate'

interface ThreadListProps {
  userId: string
}

export function ThreadList({ userId }: ThreadListProps) {
  const [page, setPage] = useState(1)
  const [isActiveFilter, setIsActiveFilter] = useState<boolean | undefined>(undefined)
  const { threads, pagination, loading, error } = useThreads(userId, page, 10, isActiveFilter)

  const handleFilterChange = (value: string) => {
    if (value === 'all') {
      setIsActiveFilter(undefined)
    } else if (value === 'active') {
      setIsActiveFilter(true)
    } else {
      setIsActiveFilter(false)
    }
    setPage(1)
  }

  if (loading) {
    return <Loading size="sm" text="스레드 로딩 중..." />
  }

  if (error) {
    return (
      <p className="text-center text-sm text-destructive">
        스레드를 불러오는데 실패했습니다
      </p>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">
          {pagination?.total ?? 0}개의 스레드
        </span>
        <Select
          value={isActiveFilter === undefined ? 'all' : isActiveFilter ? 'active' : 'inactive'}
          onChange={(e) => handleFilterChange(e.target.value)}
        >
          <option value="all">전체</option>
          <option value="active">활성 스레드</option>
          <option value="inactive">종료된 스레드</option>
        </Select>
      </div>

      {threads.length === 0 ? (
        <p className="text-center text-sm text-muted-foreground py-8">
          대화 스레드가 없습니다
        </p>
      ) : (
        <div className="divide-y">
          {threads.map((thread) => (
            <Link
              key={thread.id}
              to={`/users/${userId}/chats?thread=${thread.id}`}
              className="flex items-center justify-between py-3 transition-colors hover:bg-muted/50"
            >
              <div className="flex items-center gap-3">
                <MessageSquare className="h-5 w-5 text-muted-foreground" />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">스레드 #{thread.id}</span>
                    {thread.is_active ? (
                      <Badge variant="success">활성</Badge>
                    ) : (
                      <Badge variant="secondary">종료됨</Badge>
                    )}
                    {/* 분석 상태 배지 */}
                    {thread.analysis_status === 'processing' || thread.analysis_status === 'received' ? (
                      <Badge variant="warning" className="gap-1">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        분석 중
                      </Badge>
                    ) : thread.has_analysis ? (
                      <Badge variant="outline">분석됨</Badge>
                    ) : null}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {formatDateTime(thread.created_at)}
                    {thread.ended_at && ` - ${formatRelativeTime(thread.ended_at)}`}
                    {thread.duration_minutes && ` (${Math.round(thread.duration_minutes)}분)`}
                  </p>
                  {thread.first_message_preview && (
                    <p className="mt-1 max-w-md truncate text-sm text-muted-foreground">
                      {thread.first_message_preview}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm text-muted-foreground">
                  {thread.message_count} 메시지
                </span>
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </div>
            </Link>
          ))}
        </div>
      )}

      {pagination && pagination.total_pages > 1 && (
        <Pagination
          current={pagination.page}
          total={pagination.total_pages}
          onChange={setPage}
        />
      )}
    </div>
  )
}
